import { useState } from "react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle, X } from "lucide-react";
import { toast } from "sonner";

// XMLHttpRequest-based fetch to avoid Emergent script conflicts
const safeFetch = (url, options = {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method || 'GET', url, true);
    const headers = options.headers || {};
    Object.entries(headers).forEach(([key, value]) => {
      if (value) xhr.setRequestHeader(key, value);
    });
    xhr.onload = function() {
      let data;
      try { data = xhr.responseText ? JSON.parse(xhr.responseText) : null; }
      catch { data = xhr.responseText; }
      resolve({
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        json: () => Promise.resolve(data),
        text: () => Promise.resolve(xhr.responseText)
      });
    };
    xhr.onerror = () => reject(new Error('Network error'));
    if (options.body) {
      xhr.send(typeof options.body === 'string' ? options.body : JSON.stringify(options.body));
    } else {
      xhr.send();
    }
  });
};

const reportTypes = [
  { value: "misbehavior", label: "Misbehavior", desc: "Rude, aggressive, or inappropriate behavior" },
  { value: "safety", label: "Safety Concern", desc: "Unsafe driving or dangerous situation" },
  { value: "fraud", label: "Fraud", desc: "Overcharging, meter tampering, or scam" },
  { value: "other", label: "Other", desc: "Any other issue not listed above" },
];

export default function ReportModal({ isOpen, onClose, rideId, token }) {
  const [reportType, setReportType] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!reportType) {
      toast.error("Please select a report type");
      return;
    }
    if (!description.trim()) {
      toast.error("Please describe the issue");
      return;
    }

    setSubmitting(true);
    try {
      const res = await safeFetch(`${API}/reports`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ride_id: rideId,
          report_type: reportType,
          description: description.trim()
        })
      });

      if (res.ok) {
        toast.success("Report submitted successfully. Our team will review it.");
        onClose();
        setReportType("");
        setDescription("");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to submit report");
      }
    } catch (err) {
      toast.error("Failed to submit report");
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div 
        className="bg-white rounded-2xl w-full max-w-lg shadow-2xl" 
        data-testid="report-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h2 className="font-bold text-lg text-slate-900">Report an Issue</h2>
              <p className="text-sm text-slate-500">Ride ID: {rideId}</p>
            </div>
          </div>
          <button
            data-testid="close-report-modal"
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-5">
          {/* Report Type Selection */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              What type of issue are you reporting?
            </label>
            <div className="grid grid-cols-2 gap-3">
              {reportTypes.map((type) => (
                <button
                  key={type.value}
                  data-testid={`report-type-${type.value}`}
                  onClick={() => setReportType(type.value)}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    reportType === type.value
                      ? "border-red-500 bg-red-50"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <p className={`font-medium ${reportType === type.value ? "text-red-700" : "text-slate-900"}`}>
                    {type.label}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">{type.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Describe the issue in detail
            </label>
            <Textarea
              data-testid="report-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Please provide as much detail as possible about what happened..."
              rows={4}
              className="resize-none"
            />
          </div>

          {/* Note */}
          <p className="text-xs text-slate-500 bg-slate-50 p-3 rounded-lg">
            Your report will be reviewed by our team. We may contact you for additional information. 
            False reports may result in account suspension.
          </p>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-5 border-t border-slate-100">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            data-testid="submit-report"
            onClick={handleSubmit}
            disabled={submitting || !reportType || !description.trim()}
            className="flex-1 bg-red-600 hover:bg-red-700 text-white"
          >
            {submitting ? "Submitting..." : "Submit Report"}
          </Button>
        </div>
      </div>
    </div>
  );
}
